import { Injectable } from "@angular/core";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { environment } from "@environments/environment";
import { Job } from "../types/job.types";

// Type alias for casting if needed frequently
type EnvironmentType = typeof environment;

@Injectable({
  providedIn: "root",
})
export class SupabaseService {
  private supabase: SupabaseClient;

  constructor() {
    const env = environment as EnvironmentType;

    const isServer = typeof window === "undefined";

    this.supabase = createClient(env.supabaseUrl, env.supabaseKey, {
      auth: {
        // Disable timers and storage when running on the server to avoid
        // long-lived asynchronous tasks that keep Angular Universal from
        // finishing the rendering cycle.
        persistSession: !isServer,
        autoRefreshToken: !isServer,
        detectSessionInUrl: !isServer,
        debug: false,
      },
      db: {
        schema: "public",
      },
      global: {
        headers: {
          "x-application-name": "designjobs",
        },
      },
    });
  }

  getClient(): SupabaseClient {
    return this.supabase;
  }

  async initializeClient(): Promise<void> {
    try {
      // Only connect to realtime if needed, avoid auth operations
      // Consider if realtime is even needed at startup.
      // await this.supabase.realtime.connect();
      // Connection usually happens automatically when subscribing to a channel.
    } catch (error) {
      // Supabase connection issue (non-critical)
    }
  }

  /**
   * Get archived job by ID with location data
   * @param jobId The job ID to fetch from archived_jobs table
   * @returns Promise<Job | null>
   */
  async getArchivedJob(jobId: string): Promise<Job | null> {
    try {
      // Explicit column list (instead of *) to cut disk IO on this wide
      // table. Excludes admin-only fields the job-details page never reads
      // (status, platform, created_by, highlight_color, featured_*,
      // is_expired, archived_reason, updated_at, isfeatureduntil).
      // NOTE: must be a string literal — supabase-js parses the select
      // string at the type level and errors on dynamically built strings.
      const { data, error } = await this.supabase
        .from("archived_jobs")
        .select(
          "id, title, company, location, short_description, is_featured, salary_min, salary_max, salary_currency, full_salary, employment_type, level, work_type, category, about_company, full_description, logo_img, company_website, application_url, application_deadline, country, city, location_formatted, skills, benefits, requirements, responsibilities, created_at, posted_at, archived_at, archived_job_locations(*)"
        )
        .eq("id", jobId)
        .single();

      if (error) {
        // PGRST116 means "no rows returned" - this is expected when job doesn't exist
        // Only log other types of errors
        if (error.code !== 'PGRST116') {
          console.error("Error fetching archived job:", error);
        }
        return null;
      }

      if (data) {
        // Format the job data similar to regular jobs
        const job = data as Job;
        job.isArchived = true;

        // Map archived_job_locations to job_locations for compatibility
        if (
          data.archived_job_locations &&
          data.archived_job_locations.length > 0
        ) {
          job.job_locations = data.archived_job_locations;
          const location = data.archived_job_locations[0];
          if (location.city_geocoded && location.country_geocoded) {
            job.display_location = `${location.city_geocoded}, ${location.country_geocoded}`;
          } else if (location.location_formatted) {
            job.display_location = location.location_formatted;
          }
        }

        return job;
      }

      return null;
    } catch (error) {
      console.error("Unexpected error fetching archived job:", error);
      return null;
    }
  }
}
